import { useCallback, useEffect, useRef, useState } from 'react'

export interface Marker {
  id: string
  time: number // seconds
  label?: string
}

export interface LoopSection {
  id: string
  startMarkerId: string
  endMarkerId: string
}

export interface AudioTrack {
  role: string
  url?: string // optional - can be memory buffer
}

export interface TrackLevel {
  srcDb: number // pre-fader dBFS
  postDb?: number // post-fader dBFS
  attenuationDb?: number
}

interface UseAudioMarkerEngineProps {
  onMarkerAdded?: (marker: Marker) => void
  onLoopStarted?: (section: LoopSection) => void
  onLoopEnded?: () => void
}

/**
 * useAudioMarkerEngine - DRY audio playback engine with marker support
 * Handles:
 * - Audio loading and decoding
 * - Audio context + analyser node for visualization
 * - Playback control (play, pause, stop, seek)
 * - Marker creation via spacebar (configurable shortcut)
 * - Loop section creation and playback
 * - Time tracking and position updates
 */
export function useAudioMarkerEngine(props?: UseAudioMarkerEngineProps) {
  const { onMarkerAdded, onLoopStarted, onLoopEnded } = props || {}

  // Audio state
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(1)

  // Track/stem state (for multi-track support like Music Production)
  const [tracks, setTracks] = useState<AudioTrack[]>([])
  const [volumesByRole, setVolumesByRole] = useState<Record<string, number>>({})
  const [mutedByRole, setMutedByRole] = useState<Record<string, boolean>>({})
  const [soloByRole, setSoloByRole] = useState<Record<string, boolean>>({})
  const [levelsByRole, setLevelsByRole] = useState<Record<string, TrackLevel>>({})

  // Marker state
  const [markers, setMarkers] = useState<Marker[]>([])
  const [loopSections, setLoopSections] = useState<LoopSection[]>([])
  const [activeLoopSection, setActiveLoopSection] = useState<LoopSection | null>(null)

  // DOM + Web Audio API refs
  const audioCtxRef = useRef<AudioContext | null>(null)
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const gainRef = useRef<GainNode | null>(null)
  const bufferRef = useRef<AudioBuffer | null>(null)
  const startTimeRef = useRef<number>(0)
  const pausedTimeRef = useRef<number>(0)
  const rafRef = useRef<number | null>(null)
  const lastRafTimeRef = useRef<number>(0)
  const isPlayingRef = useRef(false)
  const lastMarkerTimeRef = useRef<number>(-1)

  // Initialize audio context
  const initAudioContext = useCallback(async () => {
    if (audioCtxRef.current) return audioCtxRef.current
    try {
      const ctx =
        new (window.AudioContext || (window as any).webkitAudioContext)()
      if (ctx.state === 'suspended') {
        await ctx.resume()
      }
      audioCtxRef.current = ctx

      // Create analyser for waveform visualization
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 2048
      analyser.smoothingTimeConstant = 0.85
      analyserRef.current = analyser

      // Create gain node for volume control
      const gain = ctx.createGain()
      gain.gain.value = volume
      gainRef.current = gain

      // Connect: gain -> analyser -> destination
      gain.connect(analyser)
      analyser.connect(ctx.destination)

      return ctx
    } catch (err) {
      setError(`Audio context error: ${err instanceof Error ? err.message : String(err)}`)
      return null
    }
  }, [volume])

  // Load audio file - now creates a default "Master" track
  const loadAudioFile = useCallback(
    async (file: File | Blob) => {
      setIsLoading(true)
      setError(null)
      try {
        const ctx = await initAudioContext()
        if (!ctx) return

        const arrayBuffer = await file.arrayBuffer()
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer)
        bufferRef.current = audioBuffer
        setDuration(audioBuffer.duration)
        setCurrentTime(0)
        pausedTimeRef.current = 0
        setMarkers([])
        setLoopSections([])
        setActiveLoopSection(null)
        
        // Create default "Master" track for the loaded audio
        const defaultTrack: AudioTrack = { role: 'Master' }
        setTracks([defaultTrack])
        setVolumesByRole({ 'Master': 1 })
        setMutedByRole({ 'Master': false })
        setSoloByRole({ 'Master': false })
        setLevelsByRole({})
      } catch (err) {
        setError(
          `Failed to load audio: ${
            err instanceof Error ? err.message : String(err)
          }`
        )
      } finally {
        setIsLoading(false)
      }
    },
    [initAudioContext]
  )

  // Load audio from a remote URL (e.g. /bff/media/... signed GCS proxy)
  const loadAudioFromUrl = useCallback(
    async (url: string, metadata?: { label?: string; role?: string }) => {
      setIsLoading(true)
      setError(null)
      try {
        const ctx = await initAudioContext()
        if (!ctx) return

        const response = await fetch(url, { credentials: 'include' })
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        const arrayBuffer = await response.arrayBuffer()
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer)
        bufferRef.current = audioBuffer
        setDuration(audioBuffer.duration)
        setCurrentTime(0)
        pausedTimeRef.current = 0
        setMarkers([])
        setLoopSections([])
        setActiveLoopSection(null)

        const role = metadata?.role ?? metadata?.label ?? 'Master'
        setTracks([{ role, url }])
        setVolumesByRole({ [role]: 1 })
        setMutedByRole({ [role]: false })
        setSoloByRole({ [role]: false })
        setLevelsByRole({})
      } catch (err) {
        setError(
          `Failed to load audio from URL: ${
            err instanceof Error ? err.message : String(err)
          }`
        )
      } finally {
        setIsLoading(false)
      }
    },
    [initAudioContext]
  )

  // Stop playback helper
  const stopPlayback = useCallback(() => {
    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.stop()
      } catch {}
      audioSourceRef.current = null
    }
    isPlayingRef.current = false
    setIsPlaying(false)
  }, [])

  // Internal play implementation that handles loop restart
  const playFrom = useCallback(
    async (startTime: number) => {
      if (!bufferRef.current) return
      try {
        const ctx = await initAudioContext()
        if (!ctx) return

        // Stop any existing playback
        stopPlayback()

        // Create new source
        const source = ctx.createBufferSource()
        source.buffer = bufferRef.current
        source.connect(gainRef.current!)

        source.start(0, startTime)
        audioSourceRef.current = source
        startTimeRef.current = ctx.currentTime - startTime
        isPlayingRef.current = true
        setIsPlaying(true)
      } catch (err) {
        setError(`Playback error: ${err instanceof Error ? err.message : String(err)}`)
        setIsPlaying(false)
      }
    },
    [initAudioContext, stopPlayback]
  )

  // Play audio from current position or loop start
  const play = useCallback(async () => {
    const loopStart = activeLoopSection
      ? markers.find((m) => m.id === activeLoopSection.startMarkerId)?.time ?? 0
      : pausedTimeRef.current
    await playFrom(loopStart)
  }, [activeLoopSection, markers, playFrom])

  // Pause audio
  const pause = useCallback(() => {
    stopPlayback()
  }, [stopPlayback])

  // Stop audio and reset to start
  const stop = useCallback(() => {
    stopPlayback()
    pausedTimeRef.current = 0
    setCurrentTime(0)
  }, [stopPlayback])

  // Seek to time
  const seek = useCallback(
    (time: number) => {
      const clampedTime = Math.max(0, Math.min(time, duration))
      pausedTimeRef.current = clampedTime
      setCurrentTime(clampedTime)

      if (isPlayingRef.current) {
        // Restart playback from new position
        stopPlayback()
        setTimeout(() => {
          playFrom(clampedTime)
        }, 10)
      }
    },
    [duration, stopPlayback, playFrom]
  )

  // Animation frame loop for position updates and loop detection
  useEffect(() => {
    if (!bufferRef.current || !isPlayingRef.current) return

    const updateTime = () => {
      rafRef.current = requestAnimationFrame(updateTime)
      const ctx = audioCtxRef.current
      if (!ctx || !isPlayingRef.current || !bufferRef.current) return

      const newTime = ctx.currentTime - startTimeRef.current
      const loopEnd = activeLoopSection
        ? markers.find((m) => m.id === activeLoopSection.endMarkerId)?.time ?? duration
        : duration

      if (newTime >= loopEnd) {
        if (activeLoopSection) {
          // Loop back to start
          const loopStart = markers.find((m) => m.id === activeLoopSection.startMarkerId)?.time ?? 0
          playFrom(loopStart)
        } else {
          // End of audio
          stopPlayback()
        }
      } else {
        setCurrentTime(newTime)
        pausedTimeRef.current = newTime
      }
    }

    rafRef.current = requestAnimationFrame(updateTime)
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [activeLoopSection, duration, markers, playFrom, stopPlayback])

  // Add marker at current position
  const addMarker = useCallback(
    (label?: string) => {
      const time = isPlayingRef.current
        ? audioCtxRef.current!.currentTime - startTimeRef.current
        : pausedTimeRef.current

      // Debounce: don't create marker if one was just created within 100ms
      if (Math.abs(time - lastMarkerTimeRef.current) < 0.1) return

      const marker: Marker = {
        id: `marker-${Date.now()}-${Math.random()}`,
        time: Math.max(0, Math.min(time, duration)),
        label,
      }
      setMarkers((prev) => [...prev, marker].sort((a, b) => a.time - b.time))
      lastMarkerTimeRef.current = time
      onMarkerAdded?.(marker)
    },
    [duration, onMarkerAdded]
  )

  // Create loop between two markers
  const createLoop = useCallback(
    (startMarkerId: string, endMarkerId: string) => {
      const startMarker = markers.find((m) => m.id === startMarkerId)
      const endMarker = markers.find((m) => m.id === endMarkerId)

      if (!startMarker || !endMarker) return
      if (startMarker.time >= endMarker.time) return

      const loopSection: LoopSection = {
        id: `loop-${Date.now()}`,
        startMarkerId,
        endMarkerId,
      }
      setLoopSections((prev) => [...prev, loopSection])
      setActiveLoopSection(loopSection)
      onLoopStarted?.(loopSection)
    },
    [markers, onLoopStarted]
  )

  // Clear active loop
  const clearLoop = useCallback(() => {
    setActiveLoopSection(null)
    onLoopEnded?.()
  }, [onLoopEnded])

  // Delete marker
  const deleteMarker = useCallback((markerId: string) => {
    setMarkers((prev) => prev.filter((m) => m.id !== markerId))
    setLoopSections((prev) =>
      prev.filter((l) => l.startMarkerId !== markerId && l.endMarkerId !== markerId)
    )
  }, [])

  // Delete loop section
  const deleteLoop = useCallback((loopId: string) => {
    if (activeLoopSection?.id === loopId) {
      setActiveLoopSection(null)
    }
    setLoopSections((prev) => prev.filter((l) => l.id !== loopId))
  }, [activeLoopSection?.id])

  // Track volume control (per role)
  const setTrackVolume = useCallback((role: string, value: number) => {
    setVolumesByRole((prev) => ({ ...prev, [role]: Math.max(0, Math.min(1, value)) }))
  }, [])

  // Track mute control
  const toggleTrackMute = useCallback((role: string) => {
    setMutedByRole((prev) => ({ ...prev, [role]: !prev[role] }))
  }, [])

  // Track solo control
  const toggleTrackSolo = useCallback((role: string) => {
    setSoloByRole((prev) => {
      const newSolo = { ...prev, [role]: !prev[role] }
      // If we enabled solo on this track, disable solo on others (single solo mode)
      if (newSolo[role]) {
        for (const key in newSolo) {
          if (key !== role) newSolo[key] = false
        }
      }
      return newSolo
    })
  }, [])

  // Get effective gain for a track (considering mute/solo)
  const getEffectiveGain = useCallback(
    (role: string): number => {
      const isMuted = mutedByRole[role] ?? false
      const soloActivated = Object.values(soloByRole).some((s) => s)
      const isSolo = soloByRole[role] ?? false

      if (isMuted) return 0
      if (soloActivated && !isSolo) return 0
      return volumesByRole[role] ?? 1
    },
    [mutedByRole, soloByRole, volumesByRole]
  )

  // Update volume
  useEffect(() => {
    if (gainRef.current) {
      gainRef.current.gain.value = volume
    }
  }, [volume])

  // Spacebar to add marker
  useEffect(() => {
    if (!bufferRef.current) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && e.target === document.body) {
        e.preventDefault()
        addMarker()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [addMarker])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      stopPlayback()
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        try {
          audioCtxRef.current.close()
        } catch {}
      }
    }
  }, [stopPlayback])

  return {
    // State
    isLoading,
    error,
    currentTime,
    duration,
    isPlaying,
    volume,
    markers,
    loopSections,
    activeLoopSection,

    // Track state
    tracks,
    volumesByRole,
    mutedByRole,
    soloByRole,
    levelsByRole,

    // Audio nodes
    analyser: analyserRef.current,
    audioContext: audioCtxRef.current,

    // Controls
    loadAudioFile,
    loadAudioFromUrl,
    play,
    pause,
    stop,
    seek,
    setVolume,

    // Track controls
    setTrackVolume,
    toggleTrackMute,
    toggleTrackSolo,
    getEffectiveGain,

    // Markers
    addMarker,
    deleteMarker,
    createLoop,
    deleteLoop,
    clearLoop,
  }
}
